import { useLoginWithLink } from '../../api/registration/hooks';
import { useLinkFamilyDevice } from '../../api/guardian/family/hooks';
/**
 * Invite Link Landing (?login=<token>)
 * Opened from the co-guardian INVITE email — opening the link accepts the
 * invite and signs the guardian in (regular logins use the emailed 6-digit
 * access code instead). Guardian logins link this browser as a family device.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  hasFamilyDeviceToken,
  setFamilyDeviceToken,
} from '../../services/family-device-storage';
import { authService } from '../../services/authentication/auth-service';
import { VersionFooter } from './version-footer';
import { FormMessage } from '../../components/form-field';
import auth from './auth.module.css';

interface GuardianLinkLandingProps {
  token: string;
}

export function GuardianLinkLanding({ token }: GuardianLinkLandingProps) {
  const loginWithLink = useLoginWithLink();
  const { mutateAsync: linkDevice } = useLinkFamilyDevice();
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    // The token is single-use — guard against React StrictMode double-effects
    if (started.current) return;
    started.current = true;

    (async () => {
      try {
        const response = await loginWithLink(token);
        authService.adoptLoginResponse(response);

        // ONLY a guardian login activates the family on this browser (links
        // it as a family device). A student's email login is personal: no
        // device link, no picker — signing out requires another login.
        if (response.user.userType === 'GUARDIAN') {
          try {
            if (!hasFamilyDeviceToken()) {
              const deviceName = t('guardian.devices.autoName', {
                name: response.user.firstName || response.user.name || 'Guardian',
              });
              const result = await linkDevice(deviceName);
              setFamilyDeviceToken(result.deviceToken);
            }
          } catch {
            // Non-fatal — the guardian can link the device from the dashboard
          }
        }

        // Drop the ?login param; the authenticated guardian lands on the dashboard
        window.location.href = window.location.pathname;
      } catch {
        setFailed(true);
      }
    })();
  }, [token, t, loginWithLink, linkDevice]);

  return (
    <div className={`${auth.loginContainer} portal-shell student-portal family-auth`}>

      <div className={auth.loginContent}>
        <div className={`${auth.loginCard} framed`}>
          {!failed ? (
            <div className={auth.loginHeader}>
              <h1 className={auth.loginTitle}>{t('linkLogin.landingTitle')}</h1>
              <p className={auth.loginSubtitle}>{t('common.loading')}</p>
            </div>
          ) : (
            <>
              <div className={auth.loginHeader}>
                <h1 className={auth.loginTitle}>{t('linkLogin.invalidTitle')}</h1>
              </div>
              <FormMessage>{t('linkLogin.invalidLink')}</FormMessage>
              <p className={auth.loginSignupHint}>
                <a href={window.location.pathname} className={auth.loginSignupLink}>
                  {t('linkLogin.requestNew')}
                </a>
              </p>
            </>
          )}
        </div>

        <div className={auth.loginVersionBox}>
          <VersionFooter />
        </div>
      </div>
    </div>
  );
}
